//RecordDetail.tsx
import React from "react";
import theme from "../../style/theme";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Button,
  useMediaQuery,
  Switch,
  Chip,
} from "@mui/material";

import { RecordDetailHeader } from "./components/RecordDetailHeader";
import { ApplicationData, FormData, ResponseActivity } from "./ApplicationData";
import { RecordDetailHeaderActions } from "./components/RecordDetailHeaderActions";
import { RecordDetailHeaderMessage } from "./components/RecordDetailHeaderMessage";
import Comment from "./components/Comment";
import dayjs from "dayjs";
import { ApprovalCommentBox } from "./components/voteComment";
import { ReportAndUpload } from "./components/ReportAndUpload";
import { useUser } from "../../context/UserContext";
import { ResponseActivityListView } from "./components/ReportedList";
import DownloadIcon from "@mui/icons-material/Download";
import ApplicationNumberList from "./components/ApplicationNumberList";
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import { Table as TiptapTable } from '@tiptap/extension-table';
import TiptapTableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TiptapTableCell from '@tiptap/extension-table-cell';
import Highlight from '@tiptap/extension-highlight';
import Color from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import Link from '@tiptap/extension-link';
import { FontSize, Indent } from '../form/RichTextToolbar';
import { OperationHistory } from "./components/OperationHistory";

/**
 * 申請詳細表示コンポーネントのプロパティ型
 */
interface ApplicationDataProps {
  applicationData: ApplicationData; // 申請データ
  handleSaveNewData: () => void; // 申請保存処理
  handleApplicationDecision: () => void; // 申請決裁処理
  handleApproveVote: (comment: string) => void; // 承認処理
  handleRejectVote: (comment: string) => void; // 却下処理
  handleSendBack: (comment: string) => void; // 差し戻し処理
  handleDownloadFile: (fileName: string) => void; // ファイルダウンロード処理
  handleSaveReportData: (report: string, files: File[], responseActivity: ResponseActivity) => void; // 報告データ保存処理
  onClickGoBackToEdit: () => void; // 編集画面に戻る処理
  handleUseThisFormat: () => void; // この申請書フォーマットを使用する処理
  handleReapplication: () => void; // 再申請処理
  handleCanceled: () => void; // 取り下げ処理
}
/**
 * 承認結果の表示テキストを取得する
 * @param decision - 承認者の決定結果（APPROVED/DENIED）
 * @param applicationStatus - 申請全体のステータス
 * @param activityStatus - 該当アクティビティのステータス
 * @returns 表示用テキスト
 */
const getDecisionText = (
  decision?: string,
  applicationStatus?: string,
  activityStatus?: string
) => {
  // 明確な決定結果がある場合
  if (decision === "APPROVED") {
    return "承認";
  }
  if (decision === "DENIED") {
    return "却下";
  }

  // 決定結果がない場合
  if (decision === null || decision === undefined) {
    // 申請がまだ開始されていない状態
    if (
      applicationStatus === "Draft" ||
      applicationStatus === "Canceled" ||
      applicationStatus === "SendBack" ||
      applicationStatus === "NotStarted"
    ) {
      return "開始前";
    }

    // 申請が進行中の場合、アクティビティのステータスで判断
    if (applicationStatus === "InProgress") {
      // アクティビティが完了または失敗している場合（他の承認者が決定済み）、投票不要
      if (activityStatus === "Completed" || activityStatus === "Failed") {
        return "ー";
      }
      // アクティビティが進行中または未開始の場合、承認待ち
      return "承認待ち";
    }

    // 申請が完了または失敗の場合、投票しなかった承認者は「ー」表示
    if (applicationStatus === "Completed" || applicationStatus === "Failed") {
      return "ー";
    }
  }

  return "";
};

function TiptapViewer({ content }: { content: string }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      FontSize,
      Indent,
      Link.configure({ openOnClick: true }),
      TiptapTable.configure({ resizable: false }),
      TiptapTableRow,
      TableHeader,
      TiptapTableCell,
      Image.configure({ allowBase64: true }),
    ],
    editable: false,     // 読み取り専用
    content: content,    // HTML をそのままセット
    editorProps: {
      attributes: {
        style: `
          height: 300px;
          overflow-y: auto;
          box-shadow: 0 0 0 1px #ddd;
          padding: 12px;
          box-sizing: border-box;
          line-height: 1.6;
          background-color: white;
        `,
      },
    },
  });

  return (
    <Box
      sx={{
        // テーブルスタイル
        "& table": {
          borderCollapse: "collapse",
          width: "100%",
          margin: "0.5em 0",
        },
        "& th, & td": {
          border: "1px solid #dee2e6",
          padding: "0.5em",
          minWidth: "50px",
        },
        "& th": {
          backgroundColor: "#f8f9fa",
          fontWeight: "bold",
        },
        // 引用スタイル
        "& blockquote": {
          borderLeft: "3px solid #ccc",
          paddingLeft: "1em",
          margin: "0.5em 0",
          color: "#666",
        },
        // コードブロックスタイル
        "& pre": {
          backgroundColor: "#f5f5f5",
          borderRadius: "4px",
          padding: "0.75em 1em",
          overflowX: "auto",
          fontFamily: "monospace",
        },
        // インラインコードスタイル
        "& code": {
          backgroundColor: "#f0f0f0",
          borderRadius: "3px",
          padding: "0.1em 0.3em",
          fontFamily: "monospace",
          fontSize: "0.9em",
        },
        // 水平線スタイル
        "& hr": {
          border: "none",
          borderTop: "2px solid #dee2e6",
          margin: "1em 0",
        },
        // 画像スタイル
        "& img": {
          maxWidth: "100%",
          height: "auto",
        },
      }}
    >
      <EditorContent editor={editor} className="prose-editor" />
    </Box>
  );
}
/**
 * 現在のユーザーが対応すべきレスポンスアクティビティを取得する
 * @param applicationData - 申請データ
 * @param userId - ユーザーID
 * @returns 対応すべきレスポンスアクティビティ、または該当なしの場合はnull
 */
function pickCurrentResponseActivity(
  applicationData: ApplicationData,
  userId?: string
): ResponseActivity | null {
  if (!applicationData || !userId) return null;

  const candidate = (applicationData.responseActivityList ?? [])
    .filter(r => {
      const scheduled = r.scheduledReporterList?.some(sr => sr.id === userId) ?? false;
      const hasValue = typeof r.value === "string" ? r.value.trim().length > 0 : !!r.value;
      return scheduled && !hasValue;
    })
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))[0];

  if (!candidate) return null;

  // activityName 補完（activityList の code=4 & InProgress）
  const act4 = (applicationData.activityList ?? [])
    .filter(a => a.code === "4" && a.status === "InProgress");

  const matchedById = candidate.activityId
    ? act4.find(a => a.activityId === candidate.activityId)
    : undefined;

  const matchedByOrder = act4.find(a => a.order === candidate.order);
  const matched = matchedById ?? matchedByOrder;

  return {
    ...candidate,
    appActivityName: matched?.activityName ?? candidate.appActivityName,
    activityId: matched?.activityId ?? candidate.activityId,
  };
}
export const RecordDetail: React.FC<ApplicationDataProps> = ({
  applicationData,
  handleSaveNewData,
  handleApplicationDecision,
  handleApproveVote,
  handleRejectVote,
  handleSendBack,
  handleDownloadFile,
  handleSaveReportData,
  onClickGoBackToEdit,
  handleUseThisFormat,
  handleReapplication,
  handleCanceled,
}) => {
  const isMobile = useMediaQuery("(max-width:600px)"); // 600px以下の場合はスマートフォンデバイス
  const { user } = useUser();
  const renderTableCell = (row: FormData) => {

    // RecordDetail.tsx - renderTableCell() 内（type === "fileUpload"）
    if (row.type === "fileUpload") {
      // 1) JSON配列（文字列）優先でパース
      const names: string[] = (() => {
        if (typeof row.value === "string") {
          const s = row.value.trim();
          // JSONならパース
          if (s.startsWith("[") || s.startsWith("{")) {
            try {
              const parsed = JSON.parse(s);
              if (Array.isArray(parsed)) {
                return parsed
                  .map(v => (typeof v === "string" ? v : String(v)))
                  .map(v => v.trim())
                  .filter(v => v.length > 0);
              }
            } catch {
              // JSONでなければ後方互換へ
            }
          }
          // 2) 後方互換：旧データ（カンマ区切り）
          return s
            .split(",")
            .map(x => x.trim())
            .filter(x => x.length > 0);
        }
        // 3) value が配列型で来た場合の保険
        if (Array.isArray(row.value)) {
          return (row.value as any[])
            .map(v => (typeof v === "string" ? v : String(v)))
            .map(v => v.trim())
            .filter(v => v.length > 0);
        }
        return [];
      })();

      return (
        <TableCell align="left" sx={{ whiteSpace: "normal" }}>
          {names.length === 0 && (
            <Typography variant="body2">（ファイルなし）</Typography>
          )}
          {names.map((name, idx) => (
            <Box key={idx} sx={{ mb: 0.5 }}>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  handleDownloadFile(name);
                }}
                style={{ color: "#1976d2", textDecoration: "underline" }}
                title="クリックしてダウンロード"
              >
                {name}
              </a>
            </Box>
          ))}
        </TableCell>
      );
    }



    if (row.type === "richText") {
      // バックエンド側の画像表示APIを呼び出せるようにURLを整形し、row.valueを更新する
      const BASE_URL = process.env.REACT_APP_BASE_URL || "";

      const convertImageSrc = (html: string) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");

        doc.querySelectorAll("img").forEach(img => {
          const src = img.getAttribute("src");
          if (src) {
            // ${BASE_URL} を置換
            const newSrc = src.replace("${BASE_URL}", BASE_URL);
            img.setAttribute("src", newSrc);
          }
        });

        return doc.body.innerHTML;
      };

      const processedContent = row.value ? convertImageSrc(row.value) : "";
      return (
        <TableCell align="left" sx={{ whiteSpace: "pre-line" }}>
          {row.value && (
            <div>
              <TiptapViewer content={processedContent} />
            </div>
          )}
        </TableCell>
      );
    }

    if (row.type === "linkList") {
      return (
        <TableCell
          align="left"
          sx={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: 300, // ← 必要に応じて調整
            verticalAlign: "top",
          }}
        >
          {row.formLinks?.map((link, linkIndex) => (
            <Box key={linkIndex} sx={{ mb: 1 }}>
              <Typography variant="body2">{link.description}</Typography>
              <Typography
                variant="body2"
                color="primary"
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                <a
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "inline-block",
                    maxWidth: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {link.url}
                </a>
              </Typography>
            </Box>
          ))}
        </TableCell>
      );
    }

    if (row.type === "applicationNumber") {
      return <ApplicationNumberList row={row} />;
    }

    if (row.type === "switch") {
      const isOn = row.value === true || row.value === "true";
      return (
        <TableCell align="left">
          <Box display="flex" alignItems="center" gap={1}>
            <Switch checked={isOn} disabled size="small" />
            <Chip
              label={isOn ? "はい" : "いいえ"}
              color={isOn ? "primary" : "default"}
              size="small"
              variant="filled"
            />
          </Box>
        </TableCell>
      );
    }


    if (row.type === "amount") {
      const value = Number(row.value ?? 0);

      return (
        <TableCell>
          {value.toLocaleString("ja-JP")}
        </TableCell>
      );
    }


    return (
      <TableCell align="left" sx={{ whiteSpace: "pre-line", wordBreak: "break-all", }}>
        {row.value?.toString()}
      </TableCell>
    );


  };
  // 「承認」/「却下」ボタンの表示・非表示を判断
  const shouldShowApprovalButtons = (() => {
    // 承認権限を持たないユーザーの場合、承認欄を表示しない
    const userAuthorities = user?.authorities ?? [];
    if (!userAuthorities.includes("approve_or_reject_requests")) {
      return false;
    }

    // 操作ボタン（照査操作）を表示すべきでない状態を除外
    if (
      !applicationData ||
      !applicationData.status ||
      applicationData.status === "Draft" ||      // 一時保存
      applicationData.status === "Canceled" ||   // 取り下げ
      applicationData.status === "Completed" ||  // 完了
      applicationData.status === "SendBack"      // 差し戻し
    ) {
      return false;
    }

    if (
      !applicationData.activityList ||
      !applicationData.approvalActivityList
    ) {
      return false;
    }

    // 1. 進行中の「照査アクティビティ」が存在するかを判断
    const inApprovalActiviy = applicationData.activityList.find(
      (activity) => activity.code === "1" && activity.status === "InProgress"
    );
    if (!inApprovalActiviy) return false;

    // 2. 進行中の「照査アクティビティ」に対応するapprovalActivityListを取得
    const approvalActivity = applicationData.approvalActivityList.find(
      (activity) => activity.order === inApprovalActiviy.order
    );
    if (!approvalActivity || !approvalActivity.approverList) return false;

    // 3. approverにログインユーザが含まれているかつ承認・却下未完成の場合
    const currentApprover = approvalActivity.approverList.find(
      (approver) => approver.id === user?.id
    );
    if (!currentApprover || currentApprover.decision) return false;

    return true;
  })();

  // 参照者を文字列として表示
  const referentStr = (() => {
    let str = "";
    if (applicationData.referent?.userList?.length) {
      str =
        str +
        applicationData.referent.userList
          .map((user) => (user.lastName ?? "") + (user.firstName ?? ""))
          .join(",");
    }
    if (applicationData.referent?.profileList?.length) {
      str = str !== "" ? str + "," : str;
      str =
        str +
        applicationData.referent.profileList
          .map((profile) => profile.value)
          .filter(
            (profileStr, index, profileValueList) =>
              profileValueList.findIndex((v) => v === profileStr) === index
          )
          .join(",");
    }
    if (applicationData.referent?.groupList?.length) {
      str = str !== "" ? str + "," : str;
      str =
        str +
        applicationData.referent.groupList.map((group) => group.name).join(",");
    }
    return str;
  })();


  // 文字列が「空白を除いて空でない」か
  const isNonEmptyString = (s?: unknown) =>
    typeof s === 'string' && s.trim().length > 0;

  // Date | string | number を安全に日付として有効か判定
  const isValidDate = (d?: unknown) => {
    if (d === null || d === undefined) return false;
    const dt = d instanceof Date ? d : new Date(d as any);
    return !isNaN(dt.getTime());
  };

  // 承認側: order===1 の承認行で、誰か1人でも「コメント or 承認日」が入っていれば実施済み
  const hasFirstApprovalDone = (() => {
    const firstApproval = (applicationData?.approvalActivityList ?? [])
      .find(a => a.order === 1);
    const approvers = firstApproval?.approverList ?? [];
    return approvers.some(ap =>
      isNonEmptyString(ap.comment) || isValidDate(ap.approvalDate)
      // もし「decision が入っていれば実施済み」にもしたい場合は下記も条件に追加
      // || !!ap.decision
    );
  })();

  // レスポンス側: order===1 のレスポンス行で「value or date」が入っていれば実施済み
  const hasFirstResponseDone = (() => {
    const firstResp = (applicationData?.responseActivityList ?? [])
      .find(r => r.order === 1);
    if (!firstResp) return false;

    // value は string 前提だが、念のため文字列化して trim 判定
    const v = typeof firstResp.value === 'string'
      ? firstResp.value
      : String(firstResp.value ?? '');

    const hasValue = isNonEmptyString(v);
    const hasDate = isValidDate(firstResp.date); // Date | string | number を許容して判定
    return hasValue || hasDate;
  })();



  const responseActivity = pickCurrentResponseActivity(applicationData, user?.id);

  return (
    <Box sx={{ mx: isMobile ? 0 : 10 }}>
      {/* 申請記録・承認管理一覧の参照画面における右上のボタン
      スマートフォンデバイスの場合はテーブルコンテナの下部に表示 */}
      {!isMobile &&
        (applicationData.status === "InProgress" ||
          applicationData.status === "Completed") && (
          applicationData.isFormatDeleted ? (
            <Box sx={{ mx: 0, mt: 8 }} />
          ) : (
            <RecordDetailHeaderActions
              status={applicationData.status}
              onClickUseThisFormat={handleUseThisFormat}
            />
          )
        )}

      {/*申請入力確認画面の独自の表示内容 */}
      {!applicationData.status && <RecordDetailHeaderMessage />}

      {/* テーブルコンテナ（白色エリア） */}
      <TableContainer
        component={Paper}
        sx={{
          width: "100%",
          pb: "1%",
          pt: "1%",
          mb: "1%",
          overflow: "hidden",
        }}
      >
        {/* 申請記録・承認管理一覧の参照画面に独自に表示する各種ラベル */}
        {
          <>
            {/* 处理 '取り下げ' 的情况 */}
            {applicationData.status &&
              applicationData.status === "Canceled" && (
                <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
                  <Typography variant="h6" color="error">
                    この申請は取り下げました。
                  </Typography>
                </Box>
              )}

            {/* その他の場合の処理 */}
            {applicationData.status &&
              applicationData.status !== "Canceled" && (
                <RecordDetailHeader applicationData={applicationData} />
              )}
          </>
        }

        <Typography variant="h2" align="center" sx={{ mb: 1 }}>
          {applicationData.type}
        </Typography>
        <Typography variant="h3" align="center" sx={{ mb: 1 }}>
          {applicationData.name}
        </Typography>
        {/* 動態的にテーブル内容を取得 */}
        <Box
          sx={{
            mt: 2,
            mx: isMobile ? 1 : 4,
            bgcolor: theme.palette.secondary.main,
            display: "flex",
            padding: isMobile ? "10px 10px" : "10px 40px",
            justifyContent: "center",
            mb: 0,
          }}
        >
          <Table sx={{ tableLayout: "fixed", width: "100%" }}>
            <TableBody sx={{ mt: 2 }}>
              {
                <TableRow key={"applicant"}>
                  <TableCell
                    align="right"
                    sx={{ width: "30%" }}
                  >{`申請者`}</TableCell>
                  <TableCell align="left" sx={{}}>
                    {(applicationData.applicant?.lastName || "") +
                      (applicationData.applicant?.firstName || "")}
                  </TableCell>
                </TableRow>
              }
              {applicationData.formEntryList
                ?.slice() // 元の配列を破壊しないようコピー
                .sort((a, b) => (a.itemOrder ?? 0) - (b.itemOrder ?? 0)) // itemOrderで昇順
                .map((row, index) => (
                  <TableRow key={index}>
                    <TableCell
                      align="right"
                      sx={{ width: "30%", verticalAlign: "top" }}
                    >
                      {row.name}
                    </TableCell>
                    {renderTableCell(row)}
                  </TableRow>
                ))}
              {
                <TableRow key={"referent"}>
                  <TableCell
                    align="right"
                    sx={{ width: "30%" }}
                  >{`参照者`}</TableCell>
                  <TableCell align="left" sx={{}}>
                    {referentStr}
                  </TableCell>
                </TableRow>
              }
            </TableBody>
          </Table>
        </Box>

        {/* 状態テーブル */}
        <Box
          sx={{
            mx: isMobile ? 1 : 4,
            backgroundColor: theme.palette.custom.lightBlue,
            padding: isMobile ? "10px 10px" : "10px 40px",
            display: "flex",
            justifyContent: "center",
          }}
        >

          <TableContainer sx={{ overflowX: isMobile ? 'auto' : 'visible', width: isMobile ? "100%" : "900px" }}>
            <Table
              sx={{ tableLayout: "fixed", width: "900px" }}
            >
              <TableHead>
                <TableRow
                  sx={{
                    borderBottom: "2px solid grey",
                    borderTop: "2px solid grey",
                  }}
                >
                  {/* 番号(カラム名) */}
                  <TableCell
                    align="center"
                    width="75px"
                    sx={{
                      borderRight: "2px solid grey",
                      borderLeft: "2px solid grey",
                    }}
                  >
                    番号
                  </TableCell>
                  {/* 承認者 (カラム名) */}
                  <TableCell
                    align="center"
                    width={isMobile ? "150px" : "250px"}
                    sx={{
                      borderRight: "2px solid grey",
                      borderLeft: "2px solid grey",
                    }}
                  >
                    承認者
                  </TableCell>
                  {/* 承認結果 (カラム名) */}
                  <TableCell
                    align="center"
                    width={isMobile ? "150px" : "100px"}
                    sx={{
                      borderRight: "2px solid grey",
                      borderLeft: "2px solid grey",
                    }}
                  >
                    承認結果
                  </TableCell>
                  {/* 承認日 (カラム名) */}
                  <TableCell
                    align="center"
                    width="200px"
                    sx={{ borderRight: "2px solid grey" }}
                  >
                    承認日
                  </TableCell>
                  {/* コメント (カラム名) */}
                  <TableCell
                    align="center"
                    width="200px"
                    sx={{
                      borderRight: "2px solid grey",
                      borderLeft: "2px solid grey",
                    }}
                  >
                    コメント
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {applicationData.approvalActivityList
                  ?.slice()
                  .sort((a, b) => a.order - b.order)
                  .map((row, index) => {
                    // 該当アクティビティのステータスを取得
                    const activityStatus = applicationData.activityList?.find(
                      (a) => a.activityId === row.activityId
                    )?.status;

                    // 承認者リストを漢字でソート
                    const sortedApproverList = row.approverList
                      ?.slice()
                      .sort((a, b) =>
                        (a.approverLastName ?? "").localeCompare(
                          b.approverLastName ?? "",
                          "ja"
                        )
                      );

                    return (
                      <TableRow
                        key={index}
                        sx={{ borderBottom: "2px solid grey", borderTop: "2px solid grey" }}
                      >
                        {/* 承認番号(順番) */}
                        <TableCell
                          align="center"
                          width="75px"
                          sx={{ borderRight: "2px solid grey", borderLeft: "2px solid grey" }}
                        >
                          {row.order}
                        </TableCell>

                        {/* 承認者名 */}
                        <TableCell
                          align="center"
                          width={isMobile ? "150px" : "250px"}
                          sx={{
                            borderRight: "2px solid grey",
                            borderLeft: "2px solid grey",
                            whiteSpace: "pre-line",
                          }}
                        >
                          {sortedApproverList
                            ?.map(
                              (approverRow) =>
                                `${approverRow.approverLastName} ${approverRow.approverFirstName}`,
                            )
                            .join("\n")}
                        </TableCell>

                        {/* 承認結果 */}
                        <TableCell
                          align="center"
                          width={isMobile ? "150px" : "100px"}
                          sx={{ borderRight: "2px solid grey", whiteSpace: "pre-line" }}
                        >
                          {sortedApproverList
                            ?.map((approverRow) =>
                              getDecisionText(
                                approverRow.decision,
                                applicationData.status,
                                activityStatus
                              ),
                            )
                            .join("\n")}
                        </TableCell>

                        {/* 承認日 */}
                        <TableCell
                          align="center"
                          width="200px"
                          sx={{ borderRight: "2px solid grey", whiteSpace: "pre-line" }}
                        >
                          {sortedApproverList
                            ?.map((approverRow) =>
                              approverRow.approvalDate && applicationData.status
                                ? dayjs(approverRow.approvalDate).format("YYYY/MM/DD HH:mm")
                                : "",
                            )
                            .join("\n")}
                        </TableCell>

                        {/* コメント */}
                        <TableCell
                          align="center"
                          width="200px"
                          sx={{
                            borderRight: "2px solid grey",
                            borderLeft: "2px solid grey",
                            whiteSpace: "pre-line",
                            wordBreak: "break-word",
                          }}
                        >
                          {sortedApproverList
                            ?.map((approverRow) =>
                              applicationData.status ? approverRow.comment ?? "" : "",
                            )
                            .join("\n")}
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>


        {/* 応答履歴 */}
        {applicationData.activityList?.some((value) => value.code === "4") && (
          <ResponseActivityListView
            responseActivityList={applicationData.responseActivityList}
          />
        )}
        {/* 応答入力欄 */}
        {responseActivity && applicationData.status === "InProgress" && (
          <Box>
            <ReportAndUpload
              responseActivity={responseActivity}
              handleSaveReportData={handleSaveReportData}
            />
          </Box>
        )}
        {/* 申請入力確認画面に独自に表示する右下ボタン */}
        {!applicationData.status && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mx: isMobile ? 1 : 4,
              my: 2,
              gap: isMobile ? 1 : 2,
              flexDirection: isMobile ? "column" : "row",
            }}
          >
            <Button
              variant="outlined"
              color="inherit"
              sx={{
                typography: "buttonMedium",
                width: isMobile ? "100%" : "auto",
              }}
              onClick={onClickGoBackToEdit}
            >
              内容修正
            </Button>
            {/* <Button
              variant="outlined"
              color="info"
              sx={{ fontSize: "100%", width: isMobile ? "100%" : "80px" }}
            >
              一時保存
            </Button> */}
            <Button
              variant="contained"
              color="primary"
              sx={{
                typography: "buttonMedium",
                width: isMobile ? "100%" : "auto",
              }}
              onClick={handleApplicationDecision}
            >
              申請確定
            </Button>
          </Box>
        )}

        {/* 差し戻し・取り下げ記録の再申請ボタン */}
        {(applicationData.status === "SendBack" ||
          applicationData.status === "Canceled") &&
          applicationData.applicant?.id === user?.id && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mx: isMobile ? 1 : 4,
                my: 2,
                gap: isMobile ? 1 : 2,
                flexDirection: isMobile ? "column" : "row",
              }}
            >
              <Button
                variant="contained"
                color="primary"
                sx={{
                  typography: "buttonMedium",
                  width: isMobile ? "100%" : "auto",
                }}
                onClick={handleReapplication}
              >
                再申請
              </Button>
            </Box>
          )}
        {/* 一時保存記録の継続編集ボタン */}
        {/* 一時保存は未提出のため、再申請と同じロジックで編集を継続する */}
        {/* applicationEntryIdを保持し、編集後に再度保存または申請する */}
        {applicationData.status === "Draft" &&
          applicationData.applicant?.id === user?.id && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mx: isMobile ? 1 : 4,
                my: 2,
                gap: isMobile ? 1 : 2,
                flexDirection: isMobile ? "column" : "row",
              }}
            >
              <Button
                variant="contained"
                color="primary"
                sx={{
                  typography: "buttonMedium",
                  width: isMobile ? "100%" : "auto",
                }}
                onClick={handleReapplication}
              >
                継続編集
              </Button>
            </Box>
          )}
        {/* 進行中申請の取り下げボタン */}
        {/* 進行中であっても */}
        {applicationData.status &&
          applicationData.status === "InProgress" &&
          applicationData.applicant?.id === user?.id &&
          !hasFirstApprovalDone &&
          !hasFirstResponseDone && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mx: isMobile ? 1 : 4,
                my: 2,
                gap: isMobile ? 1 : 2,
                flexDirection: isMobile ? "column" : "row",
              }}
            >
              <Button
                variant="outlined"
                color="primary"
                sx={{
                  typography: "buttonMedium",
                  width: isMobile ? "100%" : "auto",
                }}
                onClick={handleCanceled}
              >
                取り下げ
              </Button>
            </Box>
          )}

        {/* 申請進捗一覧参照画面に独自に表示するボタン */}
        {/* {applicationData.status && applicationData.status === "InProgress" && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mx: isMobile ? 1 : 4,
              my: 2,
              gap: isMobile ? 1 : 2,
              flexDirection: isMobile ? "column" : "row",
            }}
          >
            <Button
              variant="outlined"
              color="inherit"
              sx={{
                fontSize: "100%",
                color: "grey",
                width: isMobile ? "100%" : "80px",
              }}
              onClick={handleSaveNewData}
            >
              取り下げ
            </Button>
            <Button
              variant="contained"
              color="primary"
              sx={{ fontSize: "100%", width: isMobile ? "100%" : "80px" }}
              onClick={handleSaveNewData}
            >
              保存
            </Button>
          </Box>
        )} */}

        {/* 承認管理一覧参照画面に独自に表示するボタンとコメント欄 */}
        {shouldShowApprovalButtons && (
          <ApprovalCommentBox
            handleApproveVote={(comment) => handleApproveVote(comment)}
            handleRejectVote={(comment) => handleRejectVote(comment)}
            handleSendBack={(comment) => handleSendBack(comment)}
            applicationData={applicationData}
          />
        )}

        {/* 操作履歴 */}
        {applicationData.status && (
          <OperationHistory
            isMobile={isMobile}
            historyList={applicationData.applicationProcessHistoryList}
          />
        )
        }

      </TableContainer>

      {/* 申請記録・承認管理一覧の参照画面における右上のボタン
      スマートフォンデバイスの場合はテーブルコンテナの下部に表示 */}
      {isMobile &&
        (applicationData.status === "InProgress" ||
          applicationData.status === "Completed") && (
          applicationData.isFormatDeleted ? (
            <Box sx={{ mx: 1, my: 1.5 }} />
          ) : (
            <RecordDetailHeaderActions
              status={applicationData.status}
              onClickUseThisFormat={handleUseThisFormat}
            />
          )
        )}

      {applicationData.status && (
        <Comment applicationData={applicationData} />
      )}
    </Box>
  );
};
